import { useState } from "react"
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material"
import { UserbotTracking as T } from "../../interfaces"
import {
  useCancelJoinRequestMutation,
  useDialogQuery,
  useJoinRecoveryDetailQuery,
  useJoinRequestQuery,
} from "../../store/trackingApi"
import { useTrackingWriteAccess } from "./useTrackingWriteAccess"
import { ConfirmDialog } from "../common/confirmDialog"
import {
  dialogFields,
  recoveryFields,
  requestFields,
  safeField,
  TrackingRow,
  trackingNotes,
  trackingTitles,
} from "./safeFields"

type DetailKind = "requests" | "recovery" | "dialogs"
interface DetailProps {
  kind: DetailKind
  resource: T.AccountResource
  onClose: () => void
}

export function TrackingDetail({ kind, resource, onClose }: DetailProps) {
  const request = useJoinRequestQuery(resource, { skip: kind !== "requests" })
  const dialog = useDialogQuery(resource, { skip: kind !== "dialogs" })
  const recovery = useJoinRecoveryDetailQuery(
    { id: resource.id },
    { skip: kind !== "recovery" },
  )
  const result =
    kind === "requests" ? request : kind === "dialogs" ? dialog : recovery
  const row: TrackingRow | undefined = result.currentData
  const fields =
    kind === "requests"
      ? requestFields
      : kind === "dialogs"
        ? dialogFields
        : recoveryFields
  const canWrite = useTrackingWriteAccess()
  const [cancel, cancelState] = useCancelJoinRequestMutation()
  const [confirm, setConfirm] = useState(false)
  const [notice, setNotice] = useState("")
  const [completedConflict, setCompletedConflict] = useState(false)
  const submitCancel = async () => {
    if (!canWrite || completedConflict || cancelState.isLoading) return
    setNotice("")
    try {
      const result = await cancel(resource).unwrap()
      setNotice(
        result.status === "cancelled"
          ? "Заявка отменена; выход из Telegram не выполнялся."
          : `Заявка завершена (${result.status}); выход из Telegram не выполнялся.`,
      )
    } catch (error) {
      const err = error as { status?: unknown; data?: { message?: unknown } }
      if (
        err.status === 409 &&
        err.data?.message === "request-already-completed"
      ) {
        setCompletedConflict(true)
        setNotice(
          "request-already-completed: заявка уже завершена. Запрошено обновление данных, повторная отправка не выполняется.",
        )
      } else {
        setNotice(
          "Не удалось отменить заявку. Обновите данные и проверьте доступ.",
        )
      }
    } finally {
      setConfirm(false)
    }
  }
  return (
    <>
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{trackingTitles[kind]} — подробности</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="info">{trackingNotes[kind]}</Alert>
            <Typography variant="caption">
              {kind === "recovery"
                ? "Только безопасные метаданные"
                : `accountId: ${resource.accountId} — область данных, не ACL и не назначение сессии`}
            </Typography>
            {notice && <Alert severity="info">{notice}</Alert>}
            {result.isError && (
              <Alert severity="error">
                Ошибка загрузки. Проверьте область аккаунта и доступ. Другой
                аккаунт автоматически не используется.
              </Alert>
            )}
            {result.isFetching && <CircularProgress size={20} />}
            {!result.isError && row && (
              <Table size="small" aria-label="Безопасные подробности">
                <TableBody>
                  {fields.map((field) => (
                    <TableRow key={field}>
                      <TableCell component="th" scope="row">
                        {field}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>
                        {safeField(row, field)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => result.refetch()} disabled={result.isFetching}>
            Обновить подробности
          </Button>
          {kind === "requests" &&
            canWrite &&
            !request.isError &&
            request.currentData?.status === "pending" &&
            !completedConflict && (
              <Button
                color="error"
                onClick={() => setConfirm(true)}
                disabled={cancelState.isLoading}
              >
                Отменить заявку
              </Button>
            )}
          <Button onClick={onClose} disabled={cancelState.isLoading}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirm}
        title="Отменить отслеживание заявки?"
        description="Это завершает заявку и не выполняет выход из Telegram. Продолжить?"
        confirmText="Подтвердить отмену"
        loading={cancelState.isLoading}
        onConfirm={submitCancel}
        onCancel={() => setConfirm(false)}
      />
    </>
  )
}
